import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  AUDIT_ACTIONS,
  auditActionMeta,
  fetchAuditStats,
  listAuditLog,
  logAudit,
  type CoreAuditEntry,
} from '@/lib/coreAudit';
import Modal from '@/pages/dashboard/core/components/Modal';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

function DataBlock({ title, data }: { title: string; data: Record<string, unknown> | null }) {
  if (!data) return null;
  const entries = Object.entries(data);
  if (entries.length === 0) return null;
  return (
    <div className="rounded-md border border-background-200/70 bg-background-100/60 p-2">
      <p className="text-[10px] font-semibold text-foreground-500 uppercase tracking-wide mb-1">{title}</p>
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {entries.map(([k, v]) => (
          <span key={k} className="text-[11px] text-foreground-700"><span className="text-foreground-400">{k}=</span>{String(v)}</span>
        ))}
      </div>
    </div>
  );
}

export default function AuditTab({ org }: Props) {
  const { user } = useAuth();
  const [entries, setEntries] = useState<CoreAuditEntry[]>([]);
  const [stats, setStats] = useState<{ total: number; byAction: Record<string, number> }>({ total: 0, byAction: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [expanded, setExpanded] = useState<number | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ action: 'invoice.update', resourceType: 'invoice', resourceId: '', before: '', after: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [list, st] = await Promise.all([listAuditLog(org.id, { limit: 200 }), fetchAuditStats(org.id)]);
      setEntries(list);
      setStats(st);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(
    () => (actionFilter === 'all' ? entries : entries.filter((e) => (e.action || '').startsWith(actionFilter))),
    [entries, actionFilter],
  );

  const submit = async () => {
    if (!form.action.trim() || !form.resourceId.trim()) return;
    setSaving(true);
    try {
      const before = form.before.trim() ? { value: form.before.trim() } : null;
      const after = form.after.trim() ? { value: form.after.trim() } : null;
      await logAudit({
        tenantId: org.tenant_id,
        organizationId: org.id,
        userId: user?.id ?? null,
        userName: user?.name || 'Vous',
        action: form.action.trim(),
        resourceType: form.resourceType.trim() || null,
        resourceId: form.resourceId.trim(),
        before,
        after,
      });
      setOpen(false);
      setForm({ action: 'invoice.update', resourceType: 'invoice', resourceId: '', before: '', after: '' });
      load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-xs text-foreground-500 max-w-3xl">
          Audit Log : on enregistre les actions importantes (qui, quoi, avant / après, quand). Les logs sont
          <span className="font-semibold text-foreground-700"> protégés et non modifiables</span> par les utilisateurs standards.
        </p>
        <button onClick={() => setOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"><i className="ri-add-line"></i> Journaliser</button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {AUDIT_ACTIONS.map((a) => (
          <div key={a.key} className="rounded-lg border bg-background-50 border-background-200/70 p-3">
            <div className="flex items-center justify-between"><i className={`${a.icon} text-lg text-primary-600`}></i><span className="text-sm font-bold text-foreground-900">{stats.byAction[a.key] || 0}</span></div>
            <p className="text-xs font-medium text-foreground-700 mt-1.5">{a.label}</p>
          </div>
        ))}
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      <div className="flex flex-wrap items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
        <button onClick={() => setActionFilter('all')} className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${actionFilter === 'all' ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>Tout</button>
        {AUDIT_ACTIONS.map((a) => (
          <button key={a.key} onClick={() => setActionFilter(a.key)} className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${actionFilter === a.key ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>{a.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3"><i className="ri-file-shield-2-line text-2xl text-foreground-400"></i></div>
          <p className="text-foreground-600">Aucune entrée d’audit.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((e) => {
            const meta = auditActionMeta(e.action);
            return (
              <div key={e.id} className="bg-background-50 border border-background-200/70 rounded-lg p-3">
                <button onClick={() => setExpanded(expanded === e.id ? null : e.id)} className="w-full flex items-center gap-3 text-left cursor-pointer">
                  <span className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0"><i className={`${meta.icon} text-foreground-600`}></i></span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground-900 truncate">
                      <span className="font-semibold">{e.user_name || `Utilisateur #${e.user_id ?? '?'}`}</span>
                      <span className="text-foreground-500"> · </span>
                      <code className="text-xs text-primary-700">{e.action}</code>
                      {e.resource_id && <span className="text-foreground-500"> → {e.resource_type || 'ressource'} #{e.resource_id}</span>}
                    </p>
                    <p className="text-[11px] text-foreground-400">{e.created_at ? new Date(e.created_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                  </div>
                  <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform flex-shrink-0 ${expanded === e.id ? 'rotate-180' : ''}`}></i>
                </button>
                {expanded === e.id && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                    <DataBlock title="Avant" data={e.before_data} />
                    <DataBlock title="Après" data={e.after_data} />
                    {!e.before_data && !e.after_data && <p className="text-[11px] text-foreground-400 sm:col-span-2">Aucun détail avant/après.</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <Modal
          title="Journaliser une action"
          onClose={() => setOpen(false)}
          maxWidth="max-w-lg"
          footer={
            <>
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={submit} disabled={saving || !form.action.trim() || !form.resourceId.trim()} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>} Enregistrer
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Action (ressource.verbe)</span>
              <input className={INPUT} value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })} placeholder="Ex : invoice.update" />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Type de ressource</span>
                <input className={INPUT} value={form.resourceType} onChange={(e) => setForm({ ...form, resourceType: e.target.value })} placeholder="Ex : invoice" />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Identifiant</span>
                <input className={INPUT} value={form.resourceId} onChange={(e) => setForm({ ...form, resourceId: e.target.value })} placeholder="Ex : 123" />
              </label>
            </div>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Avant (optionnel)</span>
              <input className={INPUT} value={form.before} onChange={(e) => setForm({ ...form, before: e.target.value })} placeholder="Ex : status=pending" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Après (optionnel)</span>
              <input className={INPUT} value={form.after} onChange={(e) => setForm({ ...form, after: e.target.value })} placeholder="Ex : status=paid" />
            </label>
          </div>
        </Modal>
      )}
    </div>
  );
}